/**
 * Best callback window calculator.
 * Parses customer phone number → determines country → calculates business hours in UTC.
 * For multi-timezone countries (US, Australia), returns the overlap window where ALL timezones
 * are within business hours simultaneously.
 * Automatically accounts for DST using the browser's Intl API.
 */

// Single-timezone countries: tighter core hours
const SINGLE_TZ_START = 10; // 10 AM local
const SINGLE_TZ_END = 17;   // 5 PM local

// Multi-timezone countries: wider range for a usable overlap window
const MULTI_TZ_START = 9;   // 9 AM local
const MULTI_TZ_END = 18;    // 6 PM local

const COUNTRY_CONFIGS = {
  // North America (multi-timezone)
  '+1':   { name: 'US/Canada', timezones: ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles'], multi: true },

  // Europe
  '+44':  { name: 'UK', timezones: ['Europe/London'] },
  '+353': { name: 'Ireland', timezones: ['Europe/Dublin'] },
  '+49':  { name: 'Germany', timezones: ['Europe/Berlin'] },
  '+33':  { name: 'France', timezones: ['Europe/Paris'] },
  '+34':  { name: 'Spain', timezones: ['Europe/Madrid'] },
  '+39':  { name: 'Italy', timezones: ['Europe/Rome'] },
  '+31':  { name: 'Netherlands', timezones: ['Europe/Amsterdam'] },
  '+32':  { name: 'Belgium', timezones: ['Europe/Brussels'] },
  '+41':  { name: 'Switzerland', timezones: ['Europe/Zurich'] },
  '+43':  { name: 'Austria', timezones: ['Europe/Vienna'] },
  '+45':  { name: 'Denmark', timezones: ['Europe/Copenhagen'] },
  '+46':  { name: 'Sweden', timezones: ['Europe/Stockholm'] },
  '+47':  { name: 'Norway', timezones: ['Europe/Oslo'] },
  '+48':  { name: 'Poland', timezones: ['Europe/Warsaw'] },
  '+358': { name: 'Finland', timezones: ['Europe/Helsinki'] },
  '+30':  { name: 'Greece', timezones: ['Europe/Athens'] },
  '+351': { name: 'Portugal', timezones: ['Europe/Lisbon'] },
  '+420': { name: 'Czech Rep.', timezones: ['Europe/Prague'] },
  '+36':  { name: 'Hungary', timezones: ['Europe/Budapest'] },
  '+40':  { name: 'Romania', timezones: ['Europe/Bucharest'] },
  '+380': { name: 'Ukraine', timezones: ['Europe/Kyiv'] },

  // Asia Pacific
  '+61':  { name: 'Australia', timezones: ['Australia/Sydney', 'Australia/Perth'], multi: true },
  '+64':  { name: 'New Zealand', timezones: ['Pacific/Auckland'] },
  '+91':  { name: 'India', timezones: ['Asia/Kolkata'] },
  '+81':  { name: 'Japan', timezones: ['Asia/Tokyo'] },
  '+82':  { name: 'South Korea', timezones: ['Asia/Seoul'] },
  '+86':  { name: 'China', timezones: ['Asia/Shanghai'] },
  '+65':  { name: 'Singapore', timezones: ['Asia/Singapore'] },
  '+60':  { name: 'Malaysia', timezones: ['Asia/Kuala_Lumpur'] },
  '+63':  { name: 'Philippines', timezones: ['Asia/Manila'] },
  '+66':  { name: 'Thailand', timezones: ['Asia/Bangkok'] },
  '+62':  { name: 'Indonesia', timezones: ['Asia/Jakarta'] },
  '+84':  { name: 'Vietnam', timezones: ['Asia/Ho_Chi_Minh'] },
  '+852': { name: 'Hong Kong', timezones: ['Asia/Hong_Kong'] },
  '+886': { name: 'Taiwan', timezones: ['Asia/Taipei'] },

  // Middle East
  '+971': { name: 'UAE', timezones: ['Asia/Dubai'] },
  '+966': { name: 'Saudi Arabia', timezones: ['Asia/Riyadh'] },
  '+974': { name: 'Qatar', timezones: ['Asia/Qatar'] },
  '+972': { name: 'Israel', timezones: ['Asia/Jerusalem'] },
  '+90':  { name: 'Turkey', timezones: ['Europe/Istanbul'] },

  // Africa
  '+27':  { name: 'South Africa', timezones: ['Africa/Johannesburg'] },
  '+234': { name: 'Nigeria', timezones: ['Africa/Lagos'] },
  '+254': { name: 'Kenya', timezones: ['Africa/Nairobi'] },
  '+20':  { name: 'Egypt', timezones: ['Africa/Cairo'] },

  // Americas
  '+52':  { name: 'Mexico', timezones: ['America/Mexico_City'] },
  '+55':  { name: 'Brazil', timezones: ['America/Sao_Paulo'] },
  '+54':  { name: 'Argentina', timezones: ['America/Argentina/Buenos_Aires'] },
  '+56':  { name: 'Chile', timezones: ['America/Santiago'] },
  '+57':  { name: 'Colombia', timezones: ['America/Bogota'] },

  // Russia/CIS (capital timezone only)
  '+7':   { name: 'Russia', timezones: ['Europe/Moscow'] },
};

// Sort prefixes longest-first so +852 matches before +8, +353 before +3, etc.
const SORTED_PREFIXES = Object.keys(COUNTRY_CONFIGS).sort((a, b) => b.length - a.length);

/**
 * Get current UTC offset in minutes for a timezone. Accounts for DST automatically.
 */
function getUTCOffsetMinutes(timezone) {
  const now = new Date();
  const utcStr = now.toLocaleString('en-US', { timeZone: 'UTC' });
  const tzStr = now.toLocaleString('en-US', { timeZone: timezone });
  return (new Date(tzStr) - new Date(utcStr)) / 60000;
}

/**
 * Format total minutes (from midnight) as HH:MM
 */
function formatTime(totalMinutes) {
  const normalized = ((totalMinutes % 1440) + 1440) % 1440;
  const hours = Math.floor(normalized / 60);
  const mins = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/**
 * Get the best callback window for a customer phone number.
 *
 * Single-timezone: 10 AM – 5 PM local → UTC
 * Multi-timezone:  9 AM – 6 PM overlap across all timezones → UTC
 *
 * @param {string} phoneNumber - E.164 format, e.g. +1234567890
 * @returns {{ startUTC: string, endUTC: string, country: string, isNowGood: boolean, label: string } | null}
 */
export function getCallbackWindow(phoneNumber) {
  if (!phoneNumber) return null;

  const normalized = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;

  let config = null;
  for (const prefix of SORTED_PREFIXES) {
    if (normalized.startsWith(prefix)) {
      config = COUNTRY_CONFIGS[prefix];
      break;
    }
  }
  if (!config) return null;

  const isMulti = config.multi === true;
  const bizStart = isMulti ? MULTI_TZ_START : SINGLE_TZ_START;
  const bizEnd = isMulti ? MULTI_TZ_END : SINGLE_TZ_END;

  const offsets = config.timezones.map(tz => getUTCOffsetMinutes(tz));
  const minOffset = Math.min(...offsets); // westernmost
  const maxOffset = Math.max(...offsets); // easternmost

  // Overlap: latest business-start (westernmost) to earliest business-end (easternmost)
  const startMinutesUTC = (bizStart * 60) - minOffset;
  const endMinutesUTC = (bizEnd * 60) - maxOffset;

  const startNorm = ((startMinutesUTC % 1440) + 1440) % 1440;
  const endNorm = ((endMinutesUTC % 1440) + 1440) % 1440;

  const now = new Date();
  const nowUTCMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();

  let isNowGood;
  if (startNorm < endNorm) {
    isNowGood = nowUTCMinutes >= startNorm && nowUTCMinutes < endNorm;
  } else {
    isNowGood = nowUTCMinutes >= startNorm || nowUTCMinutes < endNorm;
  }

  // Convert UTC window to user's local timezone
  const localOffsetMinutes = -new Date().getTimezoneOffset();
  const startLocal = startNorm + localOffsetMinutes;
  const endLocal = endNorm + localOffsetMinutes;

  // Get user's timezone abbreviation (e.g. "IST", "EST", "PST")
  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const TZ_ABBR_MAP = {
    // India
    'Asia/Kolkata': 'IST', 'Asia/Calcutta': 'IST',
    // US & Canada
    'America/New_York': 'EST', 'America/Chicago': 'CST', 'America/Denver': 'MST', 'America/Los_Angeles': 'PST',
    'America/Toronto': 'EST', 'America/Vancouver': 'PST', 'America/Winnipeg': 'CST', 'America/Edmonton': 'MST',
    'America/Halifax': 'AST', 'America/Anchorage': 'AKST', 'Pacific/Honolulu': 'HST',
    // Europe
    'Europe/London': 'GMT', 'Europe/Dublin': 'GMT', 'Europe/Lisbon': 'WET',
    'Europe/Berlin': 'CET', 'Europe/Paris': 'CET', 'Europe/Madrid': 'CET', 'Europe/Rome': 'CET',
    'Europe/Amsterdam': 'CET', 'Europe/Brussels': 'CET', 'Europe/Zurich': 'CET', 'Europe/Vienna': 'CET',
    'Europe/Copenhagen': 'CET', 'Europe/Stockholm': 'CET', 'Europe/Oslo': 'CET', 'Europe/Warsaw': 'CET',
    'Europe/Prague': 'CET', 'Europe/Budapest': 'CET',
    'Europe/Helsinki': 'EET', 'Europe/Athens': 'EET', 'Europe/Bucharest': 'EET', 'Europe/Kyiv': 'EET',
    'Europe/Istanbul': 'TRT', 'Europe/Moscow': 'MSK',
    // Southeast Asia
    'Asia/Manila': 'PHT', 'Asia/Kuala_Lumpur': 'MYT', 'Asia/Singapore': 'SGT',
    'Asia/Bangkok': 'ICT', 'Asia/Ho_Chi_Minh': 'ICT', 'Asia/Jakarta': 'WIB',
    // East Asia
    'Asia/Hong_Kong': 'HKT', 'Asia/Shanghai': 'CST', 'Asia/Taipei': 'CST',
    'Asia/Tokyo': 'JST', 'Asia/Seoul': 'KST',
    // Middle East
    'Asia/Dubai': 'GST', 'Asia/Riyadh': 'AST', 'Asia/Qatar': 'AST', 'Asia/Jerusalem': 'IST',
    // Oceania
    'Australia/Sydney': 'AEST', 'Australia/Melbourne': 'AEST', 'Australia/Brisbane': 'AEST',
    'Australia/Perth': 'AWST', 'Australia/Adelaide': 'ACST',
    'Pacific/Auckland': 'NZST',
    // Africa
    'Africa/Johannesburg': 'SAST', 'Africa/Lagos': 'WAT', 'Africa/Nairobi': 'EAT', 'Africa/Cairo': 'EET',
    // Americas
    'America/Mexico_City': 'CST', 'America/Sao_Paulo': 'BRT', 'America/Argentina/Buenos_Aires': 'ART',
    'America/Santiago': 'CLT', 'America/Bogota': 'COT',
  };
  const tzAbbr = TZ_ABBR_MAP[userTz]
    || new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(new Date())
      .find(p => p.type === 'timeZoneName').value;

  return {
    start: formatTime(startLocal),
    end: formatTime(endLocal),
    country: config.name,
    isNowGood,
    label: `${formatTime(startLocal)}\u2013${formatTime(endLocal)} ${tzAbbr}`
  };
}
